import React, { useState } from "react";
import ReactECharts from "echarts-for-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { toast } from "sonner";
import { buildOption, formatNumber, PALETTE } from "@/lib/chartTheme";
import { ChartIdeas } from "@/components/bi/ChartIdeas";
import { AiThemePanel } from "@/components/bi/AiThemePanel";
import { Sparkles, SendHorizonal, Loader2, Plus, Lightbulb, Wrench, Layers, Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { configFromColumns } from "@/lib/crossSource";

const SUGGESTIONS = ["Total by category", "Trend over time", "Top 5 by value", "How many rows are in this dataset?", "What columns are available?"];
const BLEND_SUGGESTIONS = ["Revenue by customer region", "Orders per customer tier", "Monthly revenue trend by segment"];
const REFINE_SUGGESTIONS = ["Make it a pie chart", "Top 5 only", "Last 12 months", "Add average order value", "Sort descending"];
const BLEND_TYPES = [
  { value: "auto", label: "Let AI pick the chart" }, { value: "bar", label: "Bar" }, { value: "line", label: "Line" },
  { value: "pie", label: "Pie" }, { value: "donut", label: "Donut" }, { value: "kpi", label: "KPI" }, { value: "table", label: "Table" },
];

export function AiDrawer({ open, onOpenChange, datasets, defaultDatasetId, onAddWidget, theme = null }) {
  const [datasetId, setDatasetId] = useState(defaultDatasetId || "");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [blend, setBlend] = useState(false);            // blend = one chart across 2+ datasets
  const [blendIds, setBlendIds] = useState([]);
  const [blendType, setBlendType] = useState("auto");
  const [lastBlend, setLastBlend] = useState(null);      // previous answer -> follow-up refinement
  const dupNames = new Set(datasets.map((d) => d.name).filter((n, i, a) => a.indexOf(n) !== i));
  const dsLabel = (d) => dupNames.has(d.name) ? `${d.name} · ${d.mode === "live" ? "live" : "file"} · ${d.id.slice(0, 4)}` : d.name;

  React.useEffect(() => { if (defaultDatasetId) setDatasetId(defaultDatasetId); }, [defaultDatasetId]);

  const ask = async (question) => {
    const text = question || q;
    if (!text.trim()) return;
    if (!datasetId) { toast.error("Select a dataset first"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", {
        dataset_id: datasetId, question: text,
        history: history.filter((h) => h.datasetId === datasetId).slice(-4)
          .map((h) => ({ question: h.question, answer: h.answer || "" })),
      });
      setHistory((h) => [...h, { ...data, datasetId }]);
      setQ("");
    } catch (err) { toast.error(err?.response?.data?.detail || "AI failed"); }
    finally { setLoading(false); }
  };

  // Blend: one chart from 2+ sources — governed model join when they're related, cross-source SQL when not.
  // A follow-up ("make it a pie", "top 5 only") edits the previous answer instead of restarting.
  const askBlend = async (question, { refine = false } = {}) => {
    const text = question || q;
    if (!text.trim()) return;
    if (blendIds.length < 2) { toast.error("Pick at least two datasets to blend"); return; }
    const prior = refine ? lastBlend : null;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/cross-chart", {
        question: text, dataset_ids: blendIds,
        chart_type: blendType === "auto" ? null : blendType,
        previous: prior ? { mode: prior.mode, chart_type: prior.chart_type, title: prior.title, widget: prior.widget } : null,
        history: prior ? prior.history : [],
      }, { timeout: 120000 });
      const cfg = configFromColumns(data.columns || [], data.rows || [], data.chart_type);
      setLastBlend({
        mode: data.mode, chart_type: data.chart_type, title: data.title, widget: data.widget,
        history: [...((prior && prior.history) || []), text],
      });
      setHistory((h) => [...h, {
        question: text, refined: !!prior, title: data.title, chart_type: data.chart_type,
        intent: (data.mode === "federated" ? "blend · cross-source SQL" : "blend · model join") + (prior ? " · refined" : ""),
        x_axis: cfg.dimensions[0]?.alias || null, y_axis: cfg.measures.map((m) => m.alias),
        data: { columns: data.columns || [], rows: data.rows || [] },
        insight: data.reason || "", config: data.widget?.config, widget: data.widget,
      }]);
      setQ("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not build a blended chart"); }
    finally { setLoading(false); }
  };

  const addToDashboard = (item) => {
    if (item.widget) {
      onAddWidget({ ...item.widget, title: item.widget.title || item.title || item.question });
      toast.success("Added to dashboard");
      onOpenChange(false);
      return;
    }
    if (!item.config) return;
    const type = item.chart_type === "kpi" ? "kpi" : item.chart_type;
    onAddWidget({ type, title: item.title || item.question, dataset_id: item.datasetId, config: { ...item.config } });
    toast.success("Added to dashboard");
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-[color:var(--canvas)]">
        <SheetHeader className="px-5 py-4 border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]">
          <SheetTitle className="flex items-center gap-2 font-display">
            <div className="w-8 h-8 rounded-lg bg-[color:rgba(255,107,53,0.14)] flex items-center justify-center"><Sparkles className="w-4 h-4 text-[color:var(--brand-mint)]" /></div>
            Ask your data
            <span className="flex-1" />
            <Button data-testid="ai-chart-ideas-btn" size="sm" variant="outline" className="bg-[color:var(--surface)] font-sans font-normal" onClick={() => setIdeasOpen(true)}>
              <Lightbulb className="w-3.5 h-3.5 mr-1 text-[color:var(--brand-mint)]" />Chart ideas
            </Button>
          </SheetTitle>
        </SheetHeader>

        <div className="px-5 py-3 border-b border-[color:var(--glass-border)] space-y-2.5">
          {datasets.length > 1 && (
            <label className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)]">
                <Layers className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />
                Blend sources — one chart from two datasets
              </span>
              <Switch data-testid="ai-blend-toggle" checked={blend}
                onCheckedChange={(v) => { setBlend(v); if (v && blendIds.length === 0) setBlendIds(datasets.slice(0, 2).map((d) => d.id)); }} />
            </label>
          )}
          {blend ? (
            <div className="space-y-2">
              <div data-testid="ai-blend-datasets" className="flex flex-wrap gap-1.5">
                {datasets.map((d) => {
                  const on = blendIds.includes(d.id);
                  return (
                    <button key={d.id} data-testid={`ai-blend-ds-${d.id}`}
                      onClick={() => { setLastBlend(null); setBlendIds((ids) => on ? ids.filter((x) => x !== d.id) : [...ids, d.id].slice(0, 4)); }}
                      className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 border transition-colors ${on
                        ? "bg-[color:rgba(43,78,255,0.12)] border-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)]"
                        : "bg-[color:var(--surface)] border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)]"}`}>
                      {on && <Check className="w-3 h-3" />}{dsLabel(d)}
                    </button>
                  );
                })}
              </div>
              <Select value={blendType} onValueChange={setBlendType}>
                <SelectTrigger data-testid="ai-blend-type" className="bg-[color:var(--surface)] h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{BLEND_TYPES.map((t) => <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <Select value={datasetId} onValueChange={setDatasetId}>
              <SelectTrigger data-testid="ai-dataset-select" className="bg-[color:var(--surface)]"><SelectValue placeholder="Select dataset" /></SelectTrigger>
              <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{dsLabel(d)}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>

        <ScrollArea className="flex-1 px-5 py-4">
          {theme && (
            <div className="mb-4">
              <AiThemePanel {...theme} />
            </div>
          )}
          {history.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm text-[color:var(--ink-3)]">
                {blend ? "Ask for one chart that mixes the selected datasets. Try:" : "Ask a question in plain English. Try:"}
              </p>
              <div className="flex flex-wrap gap-2 justify-center mt-3">
                {(blend ? BLEND_SUGGESTIONS : SUGGESTIONS).map((s) => <button key={s} onClick={() => (blend ? askBlend(s) : ask(s))} className="text-xs px-3 py-1.5 rounded-full glass-card text-[color:var(--ink-2)] hover:text-[color:var(--brand-ocean)]">{s}</button>)}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((item, i) => <AiResult key={i} item={item} onAdd={() => addToDashboard(item)} />)}
            </div>
          )}
          {blend && lastBlend && !loading && (
            <div data-testid="ai-refine-bar" className="mt-3 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5">
              <div className="flex items-center gap-2">
                <Wrench className="w-3.5 h-3.5 text-[color:var(--brand-mint)] shrink-0" />
                <span className="text-[11px] text-[color:var(--ink-2)] truncate flex-1">
                  Refining <span className="font-medium">{lastBlend.title}</span>
                </span>
                <button data-testid="ai-refine-reset" onClick={() => setLastBlend(null)}
                  className="text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] shrink-0">Start fresh</button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {REFINE_SUGGESTIONS.map((r) => (
                  <button key={r} data-testid={`ai-refine-${r.split(" ")[0].toLowerCase()}`} onClick={() => askBlend(r, { refine: true })}
                    className="text-[10px] px-2 py-1 rounded-full bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[color:var(--ink-2)] hover:border-[color:var(--brand-mint)] hover:text-[color:var(--brand-mint)]">
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
          {loading && <div className="flex items-center gap-2 text-sm text-[color:var(--ink-3)] mt-3"><Loader2 className="w-4 h-4 animate-spin" />{blend ? (lastBlend ? "Refining the chart…" : "Blending your sources…") : "Thinking…"}</div>}
        </ScrollArea>

        <div className="p-4 border-t border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]">
          <div className="flex gap-2">
            <Input data-testid="ai-drawer-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (blend ? askBlend(null, { refine: !!lastBlend }) : ask())}
              placeholder={blend ? (lastBlend ? `Refine “${lastBlend.title}”…` : "e.g. revenue by customer region…") : "Ask anything about your data…"}
              className="bg-[color:var(--surface)]" />
            <Button data-testid="ai-drawer-send" onClick={() => (blend ? askBlend(null, { refine: !!lastBlend }) : ask())} disabled={loading} className="bg-[color:var(--brand-mint)] hover:bg-[#28a190] text-white">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizonal className="w-4 h-4" />}</Button>
          </div>
        </div>
        <ChartIdeas open={ideasOpen} onOpenChange={setIdeasOpen} datasets={datasets} defaultDatasetId={datasetId || defaultDatasetId} onAddWidget={onAddWidget} />
      </SheetContent>
    </Sheet>
  );
}

function MiniTable({ data }) {
  const cols = data?.columns?.length ? data.columns : Object.keys((data?.rows || [])[0] || {});
  const rows = (data?.rows || []).slice(0, 6);
  if (!cols.length) return <div className="h-full flex items-center justify-center text-xs text-[color:var(--ink-3)]">No rows</div>;
  return (
    <div className="h-full overflow-auto">
      <table className="w-full text-[11px]">
        <thead><tr>{cols.map((c) => <th key={c} className="text-left px-2 py-1 font-semibold text-[color:var(--ink-2)] whitespace-nowrap border-b border-[color:var(--glass-border)]">{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-b border-[color:var(--glass-border)] last:border-0">
            {cols.map((c) => <td key={c} className="px-2 py-1 text-[color:var(--ink-3)] whitespace-nowrap">{r[c] === null || r[c] === undefined ? "—" : String(r[c])}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function AiResult({ item, onAdd }) {
  const type = item.chart_type || "table";
  const isKpi = type === "kpi";
  const isTable = type === "table";
  const spec = { chart_type: type, x_axis: item.x_axis, y_axis: item.y_axis || [], series: item.series || null };
  const option = !isKpi && !isTable && item.x_axis ? buildOption(type, item.data, spec, { colors: PALETTE }) : null;
  const kpiVal = isKpi ? (item.data?.rows?.[0] || {})[(item.y_axis || [])[0]] : null;
  const isMeta = (item.intent || "").startsWith("metadata");
  return (
    <div className="glass-card p-4" data-testid="ai-result">
      <div className="flex items-center gap-2">
        <div className="text-sm font-medium text-[color:var(--ink)] flex-1">{item.title || item.question}</div>
        <Badge variant="outline" className="text-[10px] shrink-0" data-testid="ai-intent-badge">{isMeta ? <><Wrench className="w-2.5 h-2.5 mr-0.5" />{item.intent.replace("metadata.", "meta: ")}</> : item.intent}</Badge>
      </div>
      <div className="text-xs text-[color:var(--ink-3)] mt-0.5 mb-2">“{item.question}”</div>
      {item.answer && <p data-testid="ai-answer" className="text-xs text-[color:var(--ink)] bg-[color:rgba(0,194,168,0.07)] border border-[color:rgba(0,194,168,0.2)] rounded-lg px-2.5 py-1.5 mb-2 leading-relaxed">{item.answer}</p>}
      <div className="h-[200px] bg-[color:var(--glass-bg)] rounded-lg border border-[color:var(--glass-border)] p-1">
        {isKpi ? <div className="h-full flex flex-col items-center justify-center"><div className="font-display text-4xl text-[color:var(--ink)]">{formatNumber(kpiVal)}</div></div>
          : option ? <ReactECharts option={option} style={{ height: "100%", width: "100%" }} opts={{ renderer: "svg" }} notMerge />
          : <MiniTable data={item.data} />}
      </div>
      {item.insight && <p className="text-xs text-[color:var(--ink-2)] mt-2 leading-relaxed">{item.insight}</p>}
      {(item.repairs || []).filter((r) => r.how !== "deterministic").length > 0 && (
        <p className="text-[10px] text-[color:var(--ink-3)] mt-1.5">Auto-corrected: {item.repairs.filter((r) => r.how !== "deterministic").map((r) => `${r.from} → ${r.to || "removed"}`).join("; ")}</p>
      )}
      {item.config && <Button data-testid="ai-add-to-dashboard" size="sm" onClick={onAdd} className="mt-3 w-full bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white"><Plus className="w-4 h-4 mr-1" />Add to dashboard</Button>}
    </div>
  );
}
